import { IPromise } from 'angular';
import { all, call, put, select } from 'redux-saga/effects';
import * as _ from 'underscore';
import { Messages } from '../../../../codegen/Localize';
import { TextBoxMixedValues } from '../../../../ts/directives/tbTextBox';
import { IActionContext } from '../../../../ts/helpers/ActionContext';
import { ConnectionDetailInfo, DatabaseConnectionDetailInfo, RemoteConnectionDetailInfo } from '../../../../ts/models/ConnectionDetailInfo';
import { DatasourceContentType, FlowContentType, WorkbookContentType } from '../../../../ts/models/ContentTypes';
import { IContentInfo } from '../../../../ts/models/IContentInfo';
import { IDataConnectionInfo } from '../../../../ts/models/IDataConnectionInfo';
import { ExtractEncryptionState, IDataSourceBase, IDataSourceInfo } from '../../../../ts/models/IDataSourceInfo';
import { Set } from '../../../../ts/models/Set';
import { DataConnections, IConnectionUpdate } from '../../../../ts/resources/DataConnections';
import { DataSources } from '../../../../ts/resources/Datasources';
import { ICheckConnectionResult } from '../../../../ts/ServerApi/requests/CheckConnectionRequest';
import { IResult } from '../../../../ts/ServerApi/types/IResponse';
import {
  CreateExtractTasksAction,
} from '../../../../ts/services/CreateExtractTasksAction';
import { ITestConnectionResult } from '../../../../ts/services/EditConnectionAction';
import { requestFailed } from '../../../actions/RequestFailedAction';
import { EncryptionActionType } from '../../../actions/spaces/EncryptContentAction';
import { requestSpaceContents } from '../../../actions/spaces/RequestSpaceContentsActions';
import { CreateExtractsAction } from '../../../actions/spaces/RunCreateExtractsAction';
import { ServiceLocator } from '../../../ServiceLocator';
import { getCommonDatasourceInfo } from '../../../spaces/CommonDatasourceInfo';
import { encryptDataSources, encryptWorkbooks, processResult, showNotification } from '../content/EncryptContentSaga';
import { reloadSpace } from '../content/RefreshContentSagas';
import { getDataConnectionInfos } from '../EditConnectionSaga';
import { getDatasourceInfos, replaceDatasourceIdWithConnectionIdForMixedContent } from '../SpacesSagaHelpers';

export function createExtracts(
  datasources: Set<IDataSourceBase>,
  encryptionState: ExtractEncryptionState,
): IPromise<IResult> {
  // filter out the datasource which has extract or is ds.isFileBased or embedded ds refreence to published ds
  // for bridge based ds, it's only for published ds, we already disabled the button, it won't be here. 
  const filteredDs = datasources.filter(ds =>!ds.hasExtracts && !ds.isFileBased && ds.connectionType  !== 'sqlproxy');
  if(filteredDs.size() < 1) {
    return null;
  }
  const createExtractModule: CreateExtractTasksAction = ServiceLocator.get<CreateExtractTasksAction>('CreateExtractTasksAction');
  const context: IActionContext = ServiceLocator.getAngularActionContext();
  if(DataSources.isEmbedded(filteredDs.getByIndex(0))) {
    return createExtractModule.createExtractsForWorkbooks(filteredDs.getByIndex(0).workbook, filteredDs, context, encryptionState);
  } else {
    return createExtractModule.createExtractsForDatasources(filteredDs, context, encryptionState);
  }
}


export function* handleCreateExtractsAction(action: CreateExtractsAction) {
  let localizedErrorMessage = Messages.unexpectedError();
  try {
    const contentDictionaryByType = _.groupBy(action.selectedContent, c => c.contentType);
    const datasourceInfos: IDataSourceInfo[] = yield call(getDatasourceInfos, contentDictionaryByType['datasource']);
    const datasourceSet: Set<IDataSourceInfo> = Set.fromIdItemArray(datasourceInfos);
    // if datasoure has extract, we will do ecrypt or decrypt
    if(_.all(datasourceInfos, (ds: IDataSourceInfo) =>DataSources.isExtract(getCommonDatasourceInfo(ds)))) {
      const mutableContentCopy: IContentInfo[] = yield call(replaceDatasourceIdWithConnectionIdForMixedContent,datasourceInfos);
      const datasources = Set.fromIdItemArray(mutableContentCopy.filter(i => i.contentType === DatasourceContentType.name));
      const workbooks = Set.fromIdItemArray(mutableContentCopy.filter(i => i.contentType === WorkbookContentType.name));
      const encryptionActionType = action.encryptionState === ExtractEncryptionState.encrypted ?
               EncryptionActionType.encryptExtracts : EncryptionActionType.decryptExtracts;
      const [dataSourcesResult, workbooksResult] = yield all([
        call(encryptDataSources, datasources, encryptionActionType),
        call(encryptWorkbooks, workbooks, encryptionActionType),
      ]);
      const result = processResult(dataSourcesResult, workbooksResult, null);
      yield call(showNotification, result, mutableContentCopy, encryptionActionType);
    } else {
      const datasourcesService: DataSources = ServiceLocator.get<DataSources>(DataSources.ServiceLocatorKey);
      // filed based embedded datasource and embedded datasource which connected to public don't need check data connection
      const datasourceCheckSet = datasourceSet.filter((datasource) => datasource.dataConnectionsCount === 1 && datasource.testingConnectionAllowed
            && !(datasource.isFileBased || datasource.connectionType === 'sqlproxy' || datasource.connectionOAuth));

      if (datasourceCheckSet.size() > 0) {
        const results: ICheckConnectionResult[] = yield call(datasourcesService.checkConnections, datasourceCheckSet);
        if (!_.all(results, result=>result.succeeded)) {
          localizedErrorMessage = Messages.credentialMissedOrWrong();
          throw new Error(Messages.credentialMissedOrWrong());
        }
      }
      yield call(createExtracts, datasourceSet, action.encryptionState);
    }
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, localizedErrorMessage));
  }
}
